import { useCallback } from 'react';
import type { AddonCategory, SelectedAddon } from '@/modules/stores/types/addons';

import {
  applyAddonChange,
  findAddonById,
  getAddonQuantity,
  isAddonSelected,
  validateAddonSelection,
  checkMinimumRequirements,
  getMaxForAddon,
} from '../utils/addon';

export function useAddonManagement(
  addonCategories: AddonCategory[],
  selectedAddons: SelectedAddon[],
  setValue: (field: 'addons', value: SelectedAddon[]) => void
) {
  // Helpers básicos
  const getAddonData = useCallback(
    (addonId: string) => findAddonById(addonId, addonCategories),
    [addonCategories]
  );

  const getAddonQuantityById = useCallback(
    (addonId: string) => getAddonQuantity(addonId, selectedAddons),
    [selectedAddons]
  );

  const isAddonSelectedById = useCallback(
    (addonId: string) => isAddonSelected(addonId, selectedAddons),
    [selectedAddons]
  );

  // Validações
  const validateSelection = useCallback(
    (categoryId: string, newQuantity: number) =>
      validateAddonSelection(categoryId, newQuantity, selectedAddons, addonCategories),
    [selectedAddons, addonCategories]
  );

  const checkMinimum = useCallback(
    () => checkMinimumRequirements(selectedAddons, addonCategories),
    [selectedAddons, addonCategories]
  );

  const getMaxForAddonById = useCallback(
    (addonId: string, categoryId: string, selectMax: number) =>
      getMaxForAddon(addonId, categoryId, selectMax, selectedAddons, addonCategories),
    [selectedAddons, addonCategories]
  );

  // Atualização de addons (sempre imutável: nenhum objeto da seleção atual é
  // mutado in-place; o addon alterado recebe uma nova referência via spread).
  // Isso garante que a mudança de quantidade se reflita na mesma linha mesmo
  // sob memoização/comparação por referência e evita mutar fontes externas
  // (ex.: a lista vinda do cache do item em edição).
  const updateAddonsList = useCallback(
    (addonId: string, quantity: number, isRadio = false, categoryId?: string) => {
      const nextAddons = applyAddonChange(
        selectedAddons,
        addonCategories,
        addonId,
        quantity,
        isRadio,
        categoryId
      );
      if (nextAddons !== selectedAddons) setValue('addons', nextAddons);
    },
    [selectedAddons, setValue, addonCategories]
  );

  return {
    // Helpers
    getAddonData,
    getAddonQuantity: getAddonQuantityById,
    isAddonSelected: isAddonSelectedById,

    // Validações
    validateAddonSelection: validateSelection,
    checkMinimumRequirements: checkMinimum,
    getMaxForAddon: getMaxForAddonById,

    // Atualização
    updateAddonsList,
  };
}
